import React, { Component } from "react";
import {connect} from "react-redux";
import qs from "qs";
import { format } from "date-fns";

import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import Table from "@material-ui/core/Table";

import { getManagementRequest, getManagementDurationRequest } from "../../store/actions/riskAnalysis";
import { getInstrumentsRequest } from "../../store/actions/instruments";
import { getAssetsRequest } from "../../store/actions/assets";

import sortPairsFunc from '../../helpers/sortPairs';
import { renderAssetName } from '../../helpers/renderAssetName';

import {
  TableWrapper,
  MUITableCell,
  MUITableRow,
  TableContent,
  MUITableCellHalf
} from "../../components/Mui";
import Layout from "../../components/Layout";
import Preloader from "../../components/Preloader";
import SearchBar from "./components/SearchBar";

const orderInstrument = {
  hi: 1,
  lo: 2,
  hilo: 3,
  hitouch: 4,
  lotouch: 5,
  in_or_out: 6,
  double_crash: 7,
  cash_or_crash: 8
};

const getColorByRisk = (riskVal) => {
  const num = parseFloat(riskVal);
  if (isNaN(num)) return 'rgb(98 183 142)';
  if (num > 0.1) {
    return 'rgb(19, 127, 171)';
  } else if (num > 0) {
    return 'rgb(67, 189, 238)';
  } else if (num < -0.1) {
    return 'rgb(173, 29, 29)';
  } else if (num < 0) {
    return 'rgb(249, 59, 59)';
  } else {
    return 'rgb(98 183 142)';
  }
};

const getWholeInstrumentData = (instrumentsData, instrumentName, outcomeName) => {
  if (!instrumentsData || !instrumentsData[instrumentName]) return null;
  const instData = instrumentsData[instrumentName];
  if (outcomeName) {
    return instData[outcomeName] || null;
  }
  if (instData.house_profit !== undefined || instData.risk !== undefined) {
    return instData;
  }
  const keys = Object.keys(instData);
  if (keys.length > 0) {
    return instData[keys[0]];
  }
  return null;
};

const getWholeAssetData = (assetsData, assetName) => {
  if (!assetsData || !assetsData[assetName]) return null;
  return assetsData[assetName];
};

class RiskAnalysisManagement extends Component {
  state = {
    loading: false
  };

  async componentDidMount() {
    const { getInstruments, getAssets } = this.props;

    await getInstruments({ });
    await getAssets({ });

    await this.fetchRiskMatrix();

    this.fetchMatrixInterval = setInterval(() => {
      this.fetchRiskMatrix();
    }, 15000);
  }

  componentDidUpdate(prevProps) {
    const { location: { search } } = this.props;
    const { location: { search: prevSearch } } = prevProps;

    if (search !== prevSearch) {
      this.fetchRiskMatrix();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.fetchMatrixInterval);
  }

  get search() {
    const { location } = this.props;
    const { order_by } = qs.parse(location.search, { ignoreQueryPrefix: true });
    return { order_by };
  }

  fetchRiskMatrix = () => {
    const { location, getManagement } = this.props;
    const queryStr = qs.parse(location.search, { ignoreQueryPrefix: true });

    const query = location.search ?
      qs.stringify(queryStr) :
      qs.stringify({
        date_from: `${format(new Date, "yyyy-MM-dd")} 00:00:00`,
        date_to:  `${format(new Date, "yyyy-MM-dd")} 23:59:59`
      });

    getManagement({
      params: `?${query}`,
      onSuccess: () => {
        this.setState({ loading: false });
      },
      onError: () => {
        this.setState({ loading: false });
      }
    });
  };

  pushToSearch = (searchParams) => {
    const { history } = this.props;
    const search = { ...this.search };

    Object.entries(searchParams).forEach(([key, value]) => {
      if (value) search[key] = value;
      else delete search[key];
    });

    history.push(`?${qs.stringify(search)}`);
  };

  render() {
    const { instruments, assets, location, items, assetsData, instrumentsData } = this.props;
    const { loading } = this.state;
    const searchObj = qs.parse(location.search, { ignoreQueryPrefix: true });

    const sortPairs = assets && assets.length ? sortPairsFunc(assets) : [];

    const sortInstrument = instruments && instruments.length
      ? instruments.sort((l,r) =>  orderInstrument[l.name] - orderInstrument[r.name])
      : [];

    const getAssetInstruments = (itemsMap, assetName) => {
      if (!itemsMap) return [];
      if (Array.isArray(itemsMap)) {
        const assetObj = itemsMap.find(item => item.asset === assetName);
        return assetObj && assetObj.instruments ? assetObj.instruments : [];
      }
      return itemsMap[assetName] || [];
    };

    const newIntrumentsArr = sortInstrument.map(instrument => {
      const assetsArr = sortPairs.map(asset => {
        const assetInstruments = getAssetInstruments(items, asset.name);
        const instrumentObj = assetInstruments.find(r => r.name === instrument.name);
        const riskVal = instrumentObj ? instrumentObj.risk : 0;
        const houseProfit = instrumentObj ? instrumentObj.house_profit : 0;
        return ({
          name: asset.name,
          houseProfit: houseProfit,
          riskVal: `${riskVal}`,
          outcomes: instrument.outcomes && instrument.outcomes.map(outcome => {
            const outcomeObj = outcome;
            outcomeObj.asset = assetInstruments.find(r => r.name === instrument.name && r.outcome === outcome.name);
            return outcomeObj;
          }),
        });
      });

      return ({
        ...instrument,
        assets: [...assetsArr]
      });
    });

    const splitInstrumentsByOutcomes = (instrumentsArray) => {
      const result = [];
      instrumentsArray.forEach((instrument) => {
        const hasOutcomes = instrument.assets.some(function (asset) {
          return asset.outcomes && asset.outcomes.length > 0;
        });
        if (hasOutcomes) {
          const firstInstrument = Object.assign({}, instrument, {
            id: `${instrument.id}-1`,
            firstRow: true,
            assets: instrument.assets.map((asset) => {
              return Object.assign({}, asset, {
                outcomes: asset.outcomes && asset.outcomes.length > 0 ? [asset.outcomes[0]] : asset.outcomes,
              });
            }),
          });

          const secondInstrument = Object.assign({}, instrument, {
            id: `${instrument.id}-2`,
            secondRow: true,
            assets: instrument.assets.map((asset) => {
              return Object.assign({}, asset, {
                outcomes: asset.outcomes && asset.outcomes.length > 1 ? [asset.outcomes[1]] : asset.outcomes,
              });
            }),
          });
          result.push(firstInstrument, secondInstrument);
        } else {
          result.push(instrument);
        }
      });
      return result;
    };

    const getHouseProfit = (itemsMap, instrument, assetName) => {
      const assetInstruments = getAssetInstruments(itemsMap, assetName);
      const outcomeName = instrument.outcomes && instrument.outcomes[instrument.secondRow ? 1 : 0]
        ? instrument.outcomes[instrument.secondRow ? 1 : 0].name
        : null;

      const instrumentObj = assetInstruments.find(r => {
        if (r.name !== instrument.name) return false;
        if (instrument.firstRow || instrument.secondRow) {
          return outcomeName ? r.outcome === outcomeName : true;
        }
        return true;
      });
      return instrumentObj ? instrumentObj.house_profit : 0;
    };

    const getRiskVal = (itemsMap, instrument, assetName) => {
      const assetInstruments = getAssetInstruments(itemsMap, assetName);
      const outcomeName = instrument.outcomes && instrument.outcomes[instrument.secondRow ? 1 : 0]
        ? instrument.outcomes[instrument.secondRow ? 1 : 0].name
        : null;

      const instrumentObj = assetInstruments.find(r => {
        if (r.name !== instrument.name) return false;
        if (instrument.firstRow || instrument.secondRow) {
          return outcomeName ? r.outcome === outcomeName : true;
        }
        return true;
      });
      return instrumentObj ? instrumentObj.risk : 0;
    };

    return (
      <Layout fullScreen title="Risk Matrix">
        {loading && <Preloader backdrop position="fixed"/>}

        <TableWrapper>
          <SearchBar
            searchObj={searchObj}
            pushToSearch={this.pushToSearch}
            defaultValues={{
              date_from: `${format(new Date, "yyyy-MM-dd")} 00:00:00`,
              date_to: `${format(new Date, "yyyy-MM-dd")} 23:59:59`
            }}
          />

          <TableContent theme="management">
            <Table>
              <TableHead>
                <MUITableRow>
                  <MUITableCell />

                  {sortPairs.length > 0 ? (
                    <MUITableCell style={{ textAlign: 'center', fontWeight: 'bold', borderRight: '2px solid #000' }}>
                      Total
                    </MUITableCell>
                  ) : null}

                  {sortPairs.length > 0 ? sortPairs.map((pair) => (
                    <MUITableCell style={{ textAlign: 'center' }} key={pair.id}>
                      {renderAssetName(pair)}
                    </MUITableCell>
                  )) : null}
                </MUITableRow>
              </TableHead>

              <TableBody>
                {/* Whole Assets Row */}
                {sortPairs.length > 0 ? (
                  <MUITableRow style={{ borderBottom: '2px solid #000' }}>
                    <MUITableCell
                      padd="true"
                      bold="true"
                      style={{ textAlign: 'left', minWidth: 100, width: 150, fontWeight: 'bold', borderBottom: '2px solid #000' }}
                    >
                      Total
                    </MUITableCell>

                    {/* Intersection of Whole Assets row and Whole Instruments column */}
                    {(() => {
                      let totalHouseProfit = 0;
                      if (assetsData) {
                        Object.values(assetsData).forEach((data) => {
                          if (data && data.house_profit) {
                            totalHouseProfit += data.house_profit;
                          }
                        });
                      }

                      return (
                        <MUITableCell
                          style={{
                            textAlign: 'center',
                            backgroundColor: 'rgb(98 183 142)',
                            color: '#fff',
                            padding: 0,
                            width: 'auto',
                            borderRight: '2px solid #000',
                            borderBottom: '2px solid #000'
                          }}
                        >
                          <MUITableCellHalf>
                            <svg>
                              <line
                                strokeDasharray="5, 5"
                                x1="0"
                                y1="100%"
                                x2="100%"
                                y2="0"
                                style={{ stroke: '#ccc', strokeWidth: 1 }}
                              />
                            </svg>
                            <span>-</span>
                            <span>{totalHouseProfit.toFixed(1)}</span>
                          </MUITableCellHalf>
                        </MUITableCell>
                      );
                    })()}

                    {sortPairs.map((asset) => {
                      const assetData = getWholeAssetData(assetsData, asset.name);
                      const riskVal = assetData ? assetData.risk : 0;
                      const houseProfit = assetData ? assetData.house_profit : 0;
                      const bgColor = getColorByRisk(riskVal);

                      return (
                        <MUITableCell
                          style={{
                            textAlign: 'center',
                            backgroundColor: bgColor,
                            color: '#fff',
                            padding: 0,
                            width: 'auto',
                            borderBottom: '2px solid #000'
                          }}
                          key={`whole-asset-${asset.name}`}
                        >
                          <MUITableCellHalf>
                            <svg>
                              <line
                                strokeDasharray="5, 5"
                                x1="0"
                                y1="100%"
                                x2="100%"
                                y2="0"
                                style={{ stroke: '#ccc', strokeWidth: 1 }}
                              />
                            </svg>

                            <span>
                              {riskVal || riskVal === 0
                                ? `${(riskVal * 100).toFixed()}%`
                                : '-'}
                            </span>

                            <span>{(houseProfit && houseProfit.toFixed(1)) || 0}</span>
                          </MUITableCellHalf>
                        </MUITableCell>
                      );
                    })}
                  </MUITableRow>
                ) : null}

                {newIntrumentsArr.length ? splitInstrumentsByOutcomes(newIntrumentsArr).map((instrument) => (
                  <MUITableRow key={instrument.id}>
                    {instrument.outcomes && instrument.outcomes.length && instrument.firstRow ? (
                      <MUITableCell
                        padd="true"
                        bold="true"
                        style={{ textAlign: 'left', minWidth: 100, width: 150, minHeight: 105 }}
                        rowSpan={2}
                      >
                        <div style={{
                          display: 'flex',
                          width: '100%',
                        }}>
                          <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            width: '70%',
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}>
                              {instrument.title}
                            </div>
                          </div>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-around',
                            alignItems: 'flex-start',
                            width: '30%',
                            minHeight: 105,
                            borderLeft: '1px solid #ccc',
                            paddingLeft: 10,
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}>
                              {instrument.outcomes && instrument.outcomes[0] && instrument.outcomes[0].asset && instrument.outcomes[0].asset.outcome || ''}
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: 'calc(100% + 16px)',
                              height: 1,
                              backgroundColor: '#ccc',
                              marginLeft: -10,
                            }}></div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}>
                              {instrument.outcomes && instrument.outcomes[1] && instrument.outcomes[1].asset && instrument.outcomes[1].asset.outcome || ''}
                            </div>
                          </div>
                        </div>
                      </MUITableCell>
                    ) : null}

                    {!instrument.outcomes || instrument.outcomes && !instrument.outcomes.length ? (
                      <MUITableCell
                        padd="true"
                        bold="true"
                        style={{ textAlign: 'left', minWidth: 100, width: 150 }}
                      >
                        {instrument.title}
                      </MUITableCell>
                    ) : null}

                    {/* Whole Instruments Column */}
                    {(() => {
                      const outcomeName = instrument.outcomes && instrument.outcomes[instrument.secondRow ? 1 : 0]
                        ? instrument.outcomes[instrument.secondRow ? 1 : 0].name
                        : null;
                      const instData = getWholeInstrumentData(instrumentsData, instrument.name, outcomeName);
                      const riskVal = instData ? instData.risk : 0;
                      const houseProfit = instData ? instData.house_profit : 0;
                      const bgColor = getColorByRisk(riskVal);

                      return (
                        <MUITableCell
                          style={{
                            textAlign: 'center',
                            backgroundColor: bgColor,
                            color: '#fff',
                            padding: 0,
                            width: 'auto',
                            borderRight: '2px solid #000'
                          }}
                        >
                          <MUITableCellHalf>
                            <svg>
                              <line
                                strokeDasharray="5, 5"
                                x1="0"
                                y1="100%"
                                x2="100%"
                                y2="0"
                                style={{ stroke: '#ccc', strokeWidth: 1 }}
                              />
                            </svg>

                            <span>
                              {riskVal || riskVal === 0
                                ? `${(riskVal * 100).toFixed()}%`
                                : '-'}
                            </span>

                            <span>{(houseProfit && houseProfit.toFixed(1)) || 0}</span>
                          </MUITableCellHalf>
                        </MUITableCell>
                      );
                    })()}

                    {instrument.assets && instrument.assets.length > 0 ? instrument.assets.map((asset) => {
                      const assetRiskVal = getRiskVal(items, instrument, asset.name);
                      const colorTableCell = () => getColorByRisk(assetRiskVal);

                      return (
                        <MUITableCell
                          style={{
                            textAlign: 'center',
                            backgroundColor: `${colorTableCell()}`,
                            color: '#fff',
                            padding: 0,
                            width: 'auto'
                          }}
                          key={asset.name}>
                          <MUITableCellHalf>
                            <svg>
                              <line
                                strokeDasharray="5, 5"
                                x1="0"
                                y1="100%"
                                x2="100%"
                                y2="0"
                                style={{ stroke: '#ccc', strokeWidth: 1 }}/>
                            </svg>

                            <span>
                              {assetRiskVal || assetRiskVal === 0
                                ? `${(assetRiskVal * 100).toFixed()}%`
                                : '-'}
                            </span>

                            <span>{(getHouseProfit(items, instrument, asset.name) && (getHouseProfit(items, instrument, asset.name)).toFixed(1)) || 0}</span>
                          </MUITableCellHalf>
                        </MUITableCell>
                      );
                    }) : null}
                  </MUITableRow>
                )) : null}
              </TableBody>
            </Table>
          </TableContent>
        </TableWrapper>
      </Layout>
    );
  }
}

const mapState = (state) => ({
  instruments: state.instruments,
  assets: state.assets,
  items: state.riskAnalysis.items,
  assetsData: state.riskAnalysis.assets,
  instrumentsData: state.riskAnalysis.instruments
});

const mapDispatch = {
  getInstruments: getInstrumentsRequest,
  getAssets: getAssetsRequest,
  getManagement: getManagementRequest,
  getManagementDuration: getManagementDurationRequest
};

export default connect(mapState, mapDispatch)(RiskAnalysisManagement);
